import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, DollarSign, TrendingUp, Users, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDonations = () => {
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState({ total_amount: 0, count: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      // Fetch donations
      const donationsRes = await axios.get(`${BACKEND_URL}/api/donations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const donationsList = donationsRes.data || [];
      setDonations(donationsList);
      
      // Fetch stats from dedicated endpoint (only counts completed)
      const statsRes = await axios.get(`${BACKEND_URL}/api/donations/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats({
        total_amount: statsRes.data.total_amount || 0,
        count: statsRes.data.total_count || 0,
        unique_donors: statsRes.data.unique_donors || 0,
        average_donation: statsRes.data.average_donation || 0
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch donation data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear ALL donation records? This action cannot be undone.')) {
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    setIsClearing(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/donations/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "All donation records have been cleared",
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clear donation records",
        variant: "destructive"
      });
    } finally {
      setIsClearing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-[#007A7A] text-[#007A7A]"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Donation Records</h1>
          </div>
          {donations.length > 0 && (
            <Button
              onClick={handleClearAll}
              variant="destructive"
              disabled={isClearing}
              data-testid="clear-donations-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {isClearing ? 'Clearing...' : 'Clear All Records'}
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Donations</CardTitle>
              <DollarSign className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                ${stats.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-gray-600">From completed donations</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Donors</CardTitle>
              <Users className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.count}</div>
              <p className="text-xs text-gray-600">Completed donations</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Donation</CardTitle>
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                ${stats.count > 0 ? (stats.total_amount / stats.count).toFixed(2) : '0.00'}
              </div>
              <p className="text-xs text-gray-600">Per donation</p>
            </CardContent>
          </Card>
        </div>

        {/* Donations List */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Donations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              {donations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No donations recorded yet. Donations will appear here once processed through Stripe.
                </div>
              ) : (
                <table className="w-full" data-testid="donations-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Donor</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Amount</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Type</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Designation</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map((donation) => (
                      <tr key={donation.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(donation.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-gray-900">
                            {donation.donor_name || 'Anonymous'}
                          </div>
                          {donation.donor_email && (
                            <div className="text-xs text-gray-500">{donation.donor_email}</div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm font-semibold text-green-600">
                          ${donation.amount.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 capitalize">
                          {donation.donation_type}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {donation.designation || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(donation.status)}`}>
                            {donation.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDonations;
